/**
 * Market row grouping + sorting helpers.
 *
 * Identity contract: grouping/sorting must key off STRUCTURED, stable fields
 * the API provides — marketFamily/marketCategoryKey, periodScope,
 * canonicalLineKey/lineKey/line/points, lineGroupId/canonicalPairKey/
 * groupingKey, teamSide/team/normalizedSelection/selectionKey — never off a
 * market's or selection's localized DISPLAY name/label. Confirmed against a
 * real captured /markets response: market/selection names are Korean
 * strings (e.g. "1~3이닝 런라인"), so a prior version of this file that ran
 * an ENGLISH regex like /handicap/i against the market's display name to
 * decide grouping could never match in production — the exact bug class
 * already found and fixed for the category-tab filters elsewhere in this
 * codebase (see MKT_TAB_FILTER / CATEGORY_MATCHERS).
 *
 * Grouping (deriveGroupKey):
 *   - Handicap-family selections (handicap/spread/run-line/puck-line/asian
 *     handicap — all the same betting concept under different sport-specific
 *     names) pair by (period, |line|) — Home -1.5 and Away +1.5 share a row;
 *     a DIFFERENT line (e.g. -2.5) never does, even though both are "handicap".
 *   - Yes/No-family selections pair by (period, team) — home_yes/home_no
 *     share a row, away_yes/away_no share a DIFFERENT row. Never dedupe by
 *     yes/no alone or by team alone — both dimensions must survive.
 *   - Team-total selections (a total scoped to ONE team, not the whole game)
 *     pair by (period, team, line) — the home team's Over/Under must never
 *     collide with the away team's at the same numeric line.
 *   - Everything else (including player props): the API's own line/pair
 *     identity, with the line ALWAYS folded into the key even when the API's
 *     pair key doesn't already embed it, so two different lines in the same
 *     market/period can never collide into one row — and any player-identity
 *     field present is ALSO folded in, so two different players' props never
 *     collide even if they share a coarse group id and line (e.g. both
 *     "Over 20.5").
 *
 * Sorting (sortMarketRow):
 *   Over/Under  → Over left,  Under right
 *   Yes/No      → Yes left,   No right
 *   1X2 / 3-way spread → Home left,  Draw middle,  Away right
 *   Handicap    → Home left,  Away right
 */

const DRAW_RE  = /^draw$|^x$|^draw\/draw$|\bdraw\b|^무$|^무승부$/i;
const HANDICAP_FAMILY_RE = /^(handicap|spread|run[_-]?line|puck[_-]?line|asian[_-]?handicap)$/i;
const TEAM_TOTAL_FAMILY_RE = /^team[_-]?totals?$/i;
// Matched anywhere preceded by start-of-string or whitespace — NOT anchored
// to `^` alone — because a team-scoped total's selection name is prefixed
// with the team name (e.g. "콩고 민주공화국 오버 17.5"), so an anchored regex
// never matches and the sort silently falls through to the team-identity
// branch below, producing an inconsistent Over/Under left/right order.
// const OVER_RE  = /(?:^|\s)(over\b|오버)/i;
const UNDER_RE = /(?:^|\s)(under\b|언더)/i;
const OVER_RE  = /^(over\b|오버)/i;
// const UNDER_RE = /^(under\b|언더)/i;
const OVER_TOKEN_RE = /(^|[\s:_-])over($|[\s:_-])/i;
const UNDER_TOKEN_RE = /(^|[\s:_-])under($|[\s:_-])/i;
const YES_RE   = /^(yes\b|예$|네$)/i;
const NO_RE    = /^(no\b|아니오$|아니요$)/i;
const PUBLIC_ORDER_UNKNOWN = 900;

const norm = (s) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

const finiteOrder = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const publicDisplayOrderOf = (odd, index = 0) => {
  const slot = finiteOrder(odd?.displaySlot ?? odd?.display_slot);
  const order = finiteOrder(odd?.displayOrder ?? odd?.display_order);
  if (slot == null && order == null) return null;
  return {
    slot: slot ?? order ?? PUBLIC_ORDER_UNKNOWN,
    order: order ?? slot ?? PUBLIC_ORDER_UNKNOWN,
    row: finiteOrder(odd?.rowOrder ?? odd?.row_order) ?? index,
  };
};

const comparePublicDisplayOrder = (left, right, leftIndex = 0, rightIndex = 0) => {
  const leftOrder = publicDisplayOrderOf(left, leftIndex);
  const rightOrder = publicDisplayOrderOf(right, rightIndex);
  if (!leftOrder && !rightOrder) return null;
  return (leftOrder?.slot ?? PUBLIC_ORDER_UNKNOWN) - (rightOrder?.slot ?? PUBLIC_ORDER_UNKNOWN)
    || (leftOrder?.order ?? PUBLIC_ORDER_UNKNOWN) - (rightOrder?.order ?? PUBLIC_ORDER_UNKNOWN)
    || (leftOrder?.row ?? leftIndex) - (rightOrder?.row ?? rightIndex)
    || leftIndex - rightIndex;
};

export const sortSelectionsByDisplayOrder = (odds = []) => {
  const source = Array.isArray(odds) ? odds : [];
  const indexed = source.map((odd, index) => ({ odd, index, order: publicDisplayOrderOf(odd, index) }));
  if (!indexed.some((entry) => entry.order)) return [...source];
  return indexed
    .sort((left, right) => comparePublicDisplayOrder(left.odd, right.odd, left.index, right.index) ?? left.index - right.index)
    .map((entry) => entry.odd);
};

const matchesTeam = (selName, teamName) => {
  if (!teamName) return false;
  const sn = norm(selName);
  const tn = norm(teamName);
  if (!sn || !tn) return false;
  const prefixLen = Math.min(tn.length, 6);
  return sn === tn || sn.startsWith(tn.slice(0, prefixLen));
};

/** Whether a selection is the "draw" leg of a 3-way market (moneyline OR a
 * 3-way spread/handicap). Checks structured fields FIRST (teamSide/team/
 * normalizedSelection/selectionKey) — the same identity fields deriveGroupKey
 * already trusts — before falling back to the display name. A 3-way SPREAD
 * market's draw leg is exactly as likely to have a localized/non-English
 * display name as any other selection, so relying on DRAW_RE against `name`
 * alone silently fails to find it, sortMarketRow returns the row UNSORTED,
 * and the draw selection can end up rendered under the wrong (Home/Away)
 * position label instead of Draw. */
const isDrawSelection = (odd) => {
  const structured = odd.teamSide ?? odd.team ?? odd.normalizedSelection ?? odd.selectionKey ?? null;
  if (structured != null && DRAW_RE.test(String(structured))) return true;
  return DRAW_RE.test(norm(odd.name));
};

/** Pull the market's structured family key regardless of which field name
 * the payload used (both are confirmed present, identical, in real data). */
const marketFamilyOf = (market) => market?.marketFamily ?? market?.marketCategoryKey ?? null;

/** Pull the selection's line identity — NEVER by parsing its display name. */
const lineKeyOf = (odd) =>
  odd.canonicalLineKey ?? odd.lineKey
  ?? (odd.points != null ? String(odd.points) : null)
  ?? (odd.line != null ? String(odd.line) : null);

/** Pull the selection's team/side dimension — required so yes/no (and any
 * other team-scoped market) never collapses across teams. */
const teamKeyOf = (odd) => odd.teamSide ?? odd.team ?? odd.normalizedSelection ?? odd.selectionKey ?? null;

/** Pull a player/participant identity if this is a player-prop selection —
 * never inferred from the display name, since "Player Over 20.5" and
 * "OtherPlayer Over 20.5" would otherwise look identical to a purely
 * line-keyed grouping. */
const playerKeyOf = (odd) => odd.playerId ?? odd.player ?? odd.participantId ?? odd.athleteId ?? null;

const isPlaceholderLineValue = (value) => {
  if (value == null) return true;
  const normalized = String(value).trim().replace(/\s+/g, ' ').toLowerCase();
  if (!normalized) return true;
  if (['none', 'null', 'undefined', 'nan'].includes(normalized)) return true;
  return /(?:^|[:_\s-])(?:none|null|undefined|nan)$/.test(normalized);
};

const hasOverToken = (value) => {
  const text = norm(value);
  return text.includes('오버') || OVER_TOKEN_RE.test(text);
};

const hasUnderToken = (value) => {
  const text = norm(value);
  return text.includes('언더') || UNDER_TOKEN_RE.test(text);
};

const overUnderRoleOf = (odd) => {
  const structuredCandidates = [
    odd?.selectionRole,
    odd?.selection_role,
    odd?.outcomeType,
    odd?.outcome,
    odd?.normalizedSelection,
    odd?.selectionKey,
    odd?.canonicalSelectionKey,
  ];
  for (const candidate of structuredCandidates) {
    if (candidate == null) continue;
    if (hasOverToken(candidate)) return 'over';
    if (hasUnderToken(candidate)) return 'under';
  }

  const displayCandidates = [
    odd?.name,
    odd?.displayName,
    odd?.label,
    odd?.selectionName,
    odd?.selection,
  ];
  for (const candidate of displayCandidates) {
    if (candidate == null) continue;
    if (hasOverToken(candidate)) return 'over';
    if (hasUnderToken(candidate)) return 'under';
  }

  return null;
};

/**
 * Derive a stable grouping key for a selection within a market.
 * `market` should be the full market object (marketFamily/marketCategoryKey/
 * periodScope) — passing just a name string still works via the final
 * API-key/line fallback, but loses the family-aware pairing rules below.
 */
export const deriveGroupKey = (odd, market) => {
  const marketObj = (market && typeof market === 'object') ? market : null;
  const marketFamily = marketObj ? marketFamilyOf(marketObj) : null;
  const periodScope = marketObj?.periodScope ?? odd.periodScope ?? '';
  const lineKey = lineKeyOf(odd);
  const apiGroup = odd.lineGroupId ?? odd.canonicalPairKey ?? odd.groupingKey ?? null;
  const playerKey = playerKeyOf(odd);

  if (marketFamily && HANDICAP_FAMILY_RE.test(marketFamily) && lineKey != null) {
    // Prefer the API's own per-fragment pair identity (lineGroupId/
    // canonicalPairKey/groupingKey) when available — confirmed against a
    // real captured /markets response, a market can carry TWO DISTINCT
    // handicap fragments that happen to share the same |line| magnitude
    // from opposite directions (e.g. a "Home -0.25 / Away +0.25" fragment
    // AND a separate "Home +0.25 / Away -0.25" fragment). Keying purely by
    // |line| collapses all 4 of those selections into one group, which then
    // gets reordered by each odd's own displaySlot (home always first, away
    // always last) and renders as two SAME-TEAM rows instead of two
    // opposing-team pairs. The API's own pair id already scopes home+away to
    // the one specific fragment they actually belong to.
    const playerPart = playerKey != null ? `:${playerKey}` : '';
    if (apiGroup != null) {
      const groupStr = String(apiGroup);
      return playerKey != null && !groupStr.includes(String(playerKey)) ? `${groupStr}${playerPart}` : groupStr;
    }
    // Fallback: pair by (period, |line|) — structured, not parsed from a
    // label. Covers handicap/spread/run-line/puck-line/asian-handicap
    // synonyms for the same betting concept across different sports.
    return `hc:${periodScope}:${Math.abs(parseFloat(lineKey)).toFixed(2)}${playerPart}`;
  }

  if (marketFamily === 'yes_no') {
    // Pair by (period, team) so BOTH dimensions (team AND yes/no polarity)
    // survive as distinct rows instead of collapsing on either alone.
    const teamKey = teamKeyOf(odd) ?? 'default';
    return `yn:${periodScope}:${teamKey}`;
  }

  if (marketFamily && TEAM_TOTAL_FAMILY_RE.test(marketFamily) && lineKey != null) {
    // A total scoped to ONE team, not the whole game — pair by
    // (period, team, line) so the home team's Over/Under never collides
    // with the away team's at the same numeric line.
    const teamKey = teamKeyOf(odd) ?? 'default';
    return `tt:${periodScope}:${teamKey}:${lineKey}`;
  }

  if (apiGroup != null) {
    // Fold the line into the key even if the API's own group/pair key
    // doesn't already embed it — otherwise two different lines that happen
    // to share a coarse pair key (e.g. a family-level id) would collapse.
    const groupStr = String(apiGroup);
    let key = groupStr;
    if (lineKey != null && !key.includes(String(lineKey))) key = `${key}:${lineKey}`;
    // Player props: fold player identity into the key too — two different
    // players' props must never collide even if they share a coarse group
    // id and line (e.g. both "Over 20.5").
    if (playerKey != null && !key.includes(String(playerKey))) key = `${key}:${playerKey}`;
    return key;
  }

  if (lineKey != null) {
    const playerPart = playerKey != null ? `:${playerKey}` : '';
    return `${marketFamily ?? 'line'}:${periodScope}:${lineKey}${playerPart}`;
  }

  // No shared identity available at all — this selection stands alone.
  return `odd:${odd.oddStateId}`;
};

/**
 * Sort a single row of selections into canonical visual order.
 */
export const sortMarketRow = (row, home, away) => {
  if (!row || row.length <= 1) return row;

  const names = row.map((o) => norm(o.name));

  /* ── 2-way markets ──────────────────────────────── */
  if (row.length === 2) {
    const [n0, n1] = names;
    const [role0, role1] = row.map((odd) => overUnderRoleOf(odd));

    // Over / Under → Over first. This uses structured selection identity
    // first, then falls back to tokenized labels, so team-total labels like
    // "Libya Under 15.5" still land Under on the right instead of following
    // provider/WS array order.
    if (role0 === 'over' && role1 === 'under') return row;
    if (role0 === 'under' && role1 === 'over') return [row[1], row[0]];

    const apiOrdered = sortSelectionsByDisplayOrder(row);
    if (row.some((odd, index) => publicDisplayOrderOf(odd, index))) return apiOrdered;

    // Over / Under → Over first
    if (OVER_RE.test(n0) && UNDER_RE.test(n1)) return row;
    if (UNDER_RE.test(n0) && OVER_RE.test(n1)) return [row[1], row[0]];

    // Yes / No → Yes first
    if (YES_RE.test(n0) && NO_RE.test(n1)) return row;
    if (NO_RE.test(n0) && YES_RE.test(n1)) return [row[1], row[0]];

    // Handicap / team markets → Home first
    // Swap if: first item looks like away-team, OR second item looks like home-team
    const firstIsAway  = matchesTeam(n0, away);
    const secondIsHome = matchesTeam(n1, home);
    if (firstIsAway || secondIsHome) return [row[1], row[0]];

    return row;
  }

  const apiOrdered = sortSelectionsByDisplayOrder(row);
  if (row.some((odd, index) => publicDisplayOrderOf(odd, index))) return apiOrdered;

  /* ── 3-way markets (1X2 / Moneyline / 3-way spread-handicap) ───────────── */
  if (row.length === 3) {
    const drawIdx = row.findIndex((o) => isDrawSelection(o));
    if (drawIdx === -1) return row;

    const drawSel = row[drawIdx];
    const others  = row.filter((_, i) => i !== drawIdx);
    const oNames  = others.map((o) => norm(o.name));

    let homeSel = others[0];
    let awaySel = others[1];

    const firstIsAway  = matchesTeam(oNames[0], away) || matchesTeam(oNames[1], home);
    const secondIsHome = matchesTeam(oNames[1], home)  || matchesTeam(oNames[0], away);
    if (firstIsAway || secondIsHome) {
      homeSel = others[1];
      awaySel = others[0];
    }

    return [homeSel, drawSel, awaySel];
  }

  return row;
};

/** Human-readable line qualifier for a market's section title, so multiple
 * markets that share the same family/display name but different lines
 * (e.g. two "Total Points" markets, one per line) are visually
 * distinguishable instead of looking like the same market rendered twice. */
export const marketLineLabel = (market) => {
  const line = market?.line ?? market?.canonicalLineKey ?? market?.lineKey ?? null;
  if (isPlaceholderLineValue(line)) return '';
  const n = parseFloat(line);
  if (!Number.isFinite(n)) return ` (${line})`;
  return ` (${n > 0 ? '+' : ''}${n})`;
};

/** Per-selection line qualifier for a market rendered as MULTIPLE rows (one
 * per line — e.g. Totals 0.5/1.5/2.5/.../5, or several Handicap lines under
 * one section). marketLineLabel above suffixes the SECTION title for a
 * market with one overall line; this suffixes each ROW's own selection
 * label instead, so every line is individually distinguishable: "Over 0.5",
 * "Over 1.5", "Over 2", ... instead of eight identical "Over" rows differing
 * only by their odds number. Confirmed layout rule: a totals-family line has
 * NO parentheses and NO sign (Over 9.5 / Under 9.5 — both reference the same
 * bare threshold) — only handicap-family lines get parens AND a sign, since
 * home/away sit on opposite sides of the same line (Team (-1.5) / Team
 * (+1.5)). */
export const oddRowLineLabel = (odd, market) => {
  const line = odd?.canonicalLineKey ?? odd?.lineKey ?? odd?.line ?? odd?.points ?? null;
  if (isPlaceholderLineValue(line)) return '';
  const n = parseFloat(line);
  if (!Number.isFinite(n)) return ` (${line})`;

  // If the selection's own display name already spells the line out (some
  // feeds send e.g. "오버 2.5" or "FC Toktogul +0.25" pre-formatted), skip —
  // otherwise it renders duplicated ("오버 2.5 2.5" / "Team +0.25 (+0.25)").
  const ownName = String(odd?.name ?? odd?.label ?? odd?.selectionName ?? '');
  if (ownName && ownName.replace(/[+-]/g, '').includes(String(Math.abs(n)))) return '';

  const marketFamily = (market && typeof market === 'object') ? marketFamilyOf(market) : null;
  if (marketFamily != null && HANDICAP_FAMILY_RE.test(marketFamily)) {
    return ` (${n > 0 ? '+' : ''}${n})`;
  }
  return ` ${Math.abs(n)}`;
};

/** Stable per-market-TYPE identity, shared across every line of the same
 * underlying market — e.g. "Asian Total Goals" at 1.5/2.5/3.5 all carry the
 * same marketTypeKey (and, confirmed against a real captured /markets
 * response, the same marketStateId too: the API sends one array entry per
 * line under one shared market identity, not one market per line). Never
 * derived from the display name. */
const marketTypeKeyOf = (market) =>
  market?.marketTypeKey ?? market?.marketStateId ?? market?.name ?? null;

/** Merge an array of raw market objects (as the API sends them — one entry
 * per line, sharing a type identity) into one logical market per distinct
 * type, combining their selections/odds so every line becomes a ROW under
 * one shared section instead of its own repeated section. Order-preserving:
 * a type's merged entry appears at the position of its FIRST occurrence. */
export const groupMarketsByType = (markets = []) => {
  const order = [];
  const merged = new Map();
  markets.forEach((market, index) => {
    const key = marketTypeKeyOf(market) ?? `idx:${index}`;
    if (!merged.has(key)) {
      merged.set(key, { ...market, _mergedOdds: [] });
      order.push(key);
    }
    const odds = market.selections ?? market.odds ?? [];
    merged.get(key)._mergedOdds.push(...odds);
  });
  return order.map((key) => merged.get(key));
};
